import React, { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

// ─── Block types ────────────────────────────────────────────────────────────

export type ReportBlockType = 'heading' | 'subheading' | 'paragraph' | 'richtext' | 'divider' | 'custom';

export interface ReportBlock {
  id: string;
  type: ReportBlockType;
  /** Editable text content (heading / paragraph / richtext) */
  content?: string;
  /** Non-editable React element (tables, charts, custom widgets) */
  element?: ReactNode;
  /** Optional label shown above custom elements */
  label?: string;
}

export interface OpenReportOptions {
  title: string;
  subtitle?: string;
  organizationName?: string;
  blocks: ReportBlock[];
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface ReportContextType {
  isReportMode: boolean;
  reportTitle: string;
  reportSubtitle: string;
  reportOrganizationName: string;
  reportBlocks: ReportBlock[];
  fontSize: number;
  openReport: (options: OpenReportOptions) => void;
  closeReport: () => void;
  updateBlockContent: (id: string, content: string) => void;
  setFontSize: (size: number) => void;
}

const ReportContext = createContext<ReportContextType | undefined>(undefined);

// ─── Provider ────────────────────────────────────────────────────────────────

export const ReportProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isReportMode, setIsReportMode] = useState(false);
  const [reportTitle, setReportTitle] = useState('');
  const [reportSubtitle, setReportSubtitle] = useState('');
  const [reportOrganizationName, setReportOrganizationName] = useState('');
  const [reportBlocks, setReportBlocks] = useState<ReportBlock[]>([]);
  const [fontSize, setFontSize] = useState(11); // pt

  const openReport = useCallback((options: OpenReportOptions) => {
    setReportTitle(options.title);
    setReportSubtitle(options.subtitle ?? '');
    setReportOrganizationName(options.organizationName ?? '');
    setReportBlocks(options.blocks);
    setIsReportMode(true);
  }, []);

  const closeReport = useCallback(() => {
    setIsReportMode(false);
  }, []);

  const updateBlockContent = useCallback((id: string, content: string) => {
    setReportBlocks((prev) =>
      prev.map((b) => (b.id === id ? { ...b, content } : b)),
    );
  }, []);

  return (
    <ReportContext.Provider
      value={{
        isReportMode,
        reportTitle,
        reportSubtitle,
        reportOrganizationName,
        reportBlocks,
        fontSize,
        openReport,
        closeReport,
        updateBlockContent,
        setFontSize,
      }}
    >
      {children}
    </ReportContext.Provider>
  );
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

export const useReport = (): ReportContextType => {
  const ctx = useContext(ReportContext);
  if (!ctx) throw new Error('useReport must be used inside ReportProvider');
  return ctx;
};

/** Convenience hook for modules that want to launch the Report Studio */
export const useReportStudio = () => {
  const { openReport, closeReport, isReportMode } = useReport();
  return { openReport, closeReport, isReportMode };
};
